// B2: client-side mirror of the backend classify_sitemap_cause so the issue
// detail view can surface WHY a sitemap URL is flagged and what will happen.
// Keep in sync with backend/api/services/broken_link_resolver.py::classify_sitemap_cause.
// WordPress sitemaps regenerate dynamically — you fix the underlying page, not the XML.

export type SitemapCause =
	| "dead_in_sitemap"
	| "redirect_in_sitemap"
	| "noindex_in_sitemap"
	| "other";

export function classifySitemapCause(
	reason: string | null | undefined,
	statusCode: number | null | undefined,
): SitemapCause {
	const text = (reason ?? "").trim().toLowerCase();
	// Prefer the explicit reason; fall back to the status code.
	if (text.includes("4xx") || text.includes("5xx") || text.includes("not found")) {
		return "dead_in_sitemap";
	}
	if (text.includes("redirect")) return "redirect_in_sitemap";
	if (
		text.includes("noindex") ||
		text.includes("no index") ||
		text.includes("non-indexable")
	) {
		return "noindex_in_sitemap";
	}

	if (statusCode !== null && statusCode !== undefined) {
		const code = Number(statusCode);
		if (!Number.isNaN(code)) {
			if (code >= 400) return "dead_in_sitemap";
			if (code >= 300 && code < 400) return "redirect_in_sitemap";
		}
	}
	return "other";
}

export interface SitemapCauseMeta {
	label: string;
	explanation: string;
	/** Whether the backend auto-applies a fix (only dead URLs get a 301). */
	autoFixable: boolean;
	actionLabel: string;
}

export const SITEMAP_CAUSE_META: Record<SitemapCause, SitemapCauseMeta> = {
	dead_in_sitemap: {
		label: "Dead URL in sitemap (4xx/5xx)",
		explanation:
			"The sitemap lists a dead page. Fix = 301-redirect it to the nearest live page; the sitemap drops it automatically on the next crawl.",
		autoFixable: true,
		actionLabel: "Redirect to nearest live page",
	},
	redirect_in_sitemap: {
		label: "Redirecting URL in sitemap",
		explanation:
			"The sitemap lists a URL that now redirects. It should list the final destination — this self-corrects after a sitemap resubmit/recheck, not a per-page edit.",
		autoFixable: false,
		actionLabel: "Resubmit & recheck (review-only)",
	},
	noindex_in_sitemap: {
		label: "Noindex page in sitemap",
		explanation:
			"A noindex page is wrongly in the sitemap — usually intentional. Review only; remove the noindex manually if it should rank.",
		autoFixable: false,
		actionLabel: "Review-only",
	},
	other: {
		label: "Other / unknown",
		explanation: "Cause not confidently recognized — review manually before acting.",
		autoFixable: false,
		actionLabel: "Review-only",
	},
};
